import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import queryString from 'query-string'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import styled from 'styled-components'
import { TextCallout1, IconBbf, IconHistory } from 'blocket-ui'
import { actionCreators as searchParamsActionCreators } from 'reduxBits/searchParams'
import featuresPlugin from '@blazar-plugins/features'
import { mediaQueries, ie11media } from '@blazar-plugins/device'
import FilterComponent from './FilterComponent'
import DropdownLocation from './FilterComponent/Collection/DropdownCollection'
import { getFilterPlacements } from './selectors'

const Wrapper = styled.div``

const TopRadioWrapper = styled.div`
  padding: 8px 0;
  display: flex;
  align-items: center;
  border-top: 0.5px solid #efefef;

  @media (max-width: 980px) {
    padding-left: 16px;
    padding-right: 16px;
  }

  .filter-flex {
    display: flex;
    align-items: center;
    label {
      margin-left: 16px;
    }
  }

  .filter:first-child {
    margin-right: 40px;
  }
`

const BooleanPlacementWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-top: 0.5px solid #efefef;

  @media (max-width: 980px) {
    padding-left: 16px;
    padding-right: 16px;
  }

  ${ie11media} {
    clear: left;
  }
`

const BooleansWrapper = styled.div`
  display: flex;
`

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-column-gap: 16px;
  padding: 8px 0;
  border-top: 0.5px solid #efefef;

  @media (max-width: 980px) {
    padding-left: 16px;
    padding-right: 16px;
  }

  ${ie11media} {
    .filter {
      float: left;
      width: 147px;
      margin-right: 16px;
    }
  }
`

/**
 * We want to create a grid that looks like
 *
 * medium (tablet):
 * [    ]
 * [ ][ ]
 *
 * large (desktop):
 * [    ][ ][ ]
 */
const SpecialWrapper = styled.div`
  display: grid;
  grid-column-gap: 16px;
  column-gap: 16px;
  grid-row-gap: 8px;
  row-gap: 8px;
  padding: 8px 0;
  border-top: 0.5px solid #efefef;

  @media (max-width: 980px) {
    padding-left: 16px;
    padding-right: 16px;
  }

  grid-template-columns: 1fr 1fr;
  .filter:first-child {
    grid-column: 1 / 3;
  }
  @media ${mediaQueries.large} {
    grid-template-columns: 1fr 1fr 150px 150px;
  }

  ${DropdownLocation} {
    max-width: 150px;
  }

  ${ie11media} {
    .filter {
      float: left;
    }
  }
`

const BooleanHeader = styled(TextCallout1)`
  margin-right: 16px;
`

const mapStateToProps = (state, ownProps) => {
  const currentQuery = queryString.parse(ownProps.location.search)

  return {
    currentQuery,
    filters: getFilterPlacements(state, currentQuery),
    isPremiumCheckTooltipEnabled: featuresPlugin.selectors.isEnabled(
      state,
      'premium-check-tooltip',
    ),
  }
}

const mapDispatchToProps = (dispatch) => ({
  searchParamsActions: bindActionCreators(searchParamsActionCreators, dispatch),
})

const filterIdToChildren = {
  cars_free_carfax: <IconHistory width={18} height={18} />,
  car_insurance: <IconBbf width={18} height={18} />,
  cars_premium_warranty: <IconBbf width={18} height={18} />,
}

const _renderFilters = (filters, onChange, currentQuery, isPremiumCheckTooltipEnabled) => {
  return (
    <React.Fragment>
      {filters.map((filter) => {
        return (
          <div className={`filter ${filter.id}`} key={filter.id} data-cy={`filter-${filter.id}`}>
            <FilterComponent
              {...filter}
              onChange={onChange}
              currentQuery={currentQuery}
              isPremiumCheckTooltipEnabled={isPremiumCheckTooltipEnabled}
            >
              {filterIdToChildren[filter.id]}
            </FilterComponent>
          </div>
        )
      })}
    </React.Fragment>
  )
}

export const FilterView = ({
  filters,
  searchParamsActions,
  currentQuery,
  isPremiumCheckTooltipEnabled,
}) => {
  const handleChange = useCallback(
    (e, queryKey, value) => {
      // If the value is empty string, set it to undefined
      // so that the redux action removes it from the query
      const _value = value === '' ? undefined : value
      searchParamsActions.select({ [queryKey]: _value, page: undefined })
    },
    [searchParamsActions],
  )

  return (
    <Wrapper data-cy="filter-wrapper">
      {filters.placements.TOP.length > 0 && (
        <TopRadioWrapper>
          {_renderFilters(filters.placements.TOP, handleChange, currentQuery)}
          {filters.placements.TOP_BOOLEAN.length > 0 && <BooleanHeader>Visa endast:</BooleanHeader>}
          {_renderFilters(filters.placements.TOP_BOOLEAN, handleChange, currentQuery)}
        </TopRadioWrapper>
      )}

      {filters.placements.GRID.length > 0 && (
        <GridWrapper>
          {_renderFilters(filters.placements.GRID, handleChange, currentQuery)}
        </GridWrapper>
      )}

      {filters.placements.BOOLEAN.length > 0 && (
        <BooleanPlacementWrapper>
          {filters.booleanHeader && <BooleanHeader>{filters.booleanHeader}</BooleanHeader>}
          <BooleansWrapper>
            {_renderFilters(
              filters.placements.BOOLEAN,
              handleChange,
              currentQuery,
              isPremiumCheckTooltipEnabled,
            )}
          </BooleansWrapper>
        </BooleanPlacementWrapper>
      )}

      {filters.placements.SPECIAL.length > 0 && (
        <SpecialWrapper>
          {_renderFilters(filters.placements.SPECIAL, handleChange, currentQuery)}
        </SpecialWrapper>
      )}
    </Wrapper>
  )
}

FilterView.propTypes = {
  filters: PropTypes.object.isRequired,
  searchParamsActions: PropTypes.shape({
    select: PropTypes.func.isRequired,
    saveFilterParameters: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }).isRequired,
  currentQuery: PropTypes.object.isRequired,
  isPremiumCheckTooltipEnabled: PropTypes.bool,
}

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(FilterView),
)
